import React, { ReactNode } from 'react';
import { Checkbox, RadioButton, TableCell } from 'src/components/atoms';
import { TableRowStyled } from './TableRow.styles';

export interface ColumnType {
  label: string;
  value?: string | number;
  showOverflow?: boolean;
}

type TableRowSharedProps = {
  isSelected?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  cellData: (ReactNode | string | number)[];
  allowRowSelect?: boolean;
  selectionGroupName?: string;
  'data-testid'?: string;
  fixedRowHeight?: boolean;
  columns: ColumnType[];
  visibleColumns?: ColumnType[];
  maxColumnWidth?: string;
  columnsWidths?: (string | undefined)[];
};

type TableRowCheckboxProps = TableRowSharedProps & {
  selectionVariant?: 'checkbox';
  rowKey?: string;
};

type TableRowRadioProps = TableRowSharedProps & {
  selectionVariant: 'radio';
  rowKey: string;
};

export type TableRowProps = TableRowCheckboxProps | TableRowRadioProps;

const sanitizeDomIdPart = (value: string) =>
  value.replace(/[^a-zA-Z0-9_-]/g, '_');

export const TableRow = ({
  columns,
  visibleColumns = columns,
  selectionVariant = 'checkbox',
  selectionGroupName,
  rowKey,
  ...props
}: TableRowProps) => {
  const rowTestId = props['data-testid'] ?? 'table-row';

  return (
    <TableRowStyled {...props} isClickable={Boolean(props.allowRowSelect)}>
      {props.allowRowSelect && selectionVariant === 'checkbox' && (
        <TableCell checkboxMargin>
          <Checkbox
            checked={Boolean(props.isSelected)}
            onChange={() => {}}
            data-testid={`${rowTestId}-checkbox`}
          />
        </TableCell>
      )}
      {props.allowRowSelect && selectionVariant === 'radio' && (
        <TableCell checkboxMargin>
          <RadioButton
            id={`table-row-radio-${sanitizeDomIdPart(
              props['data-testid'] ?? rowKey ?? '',
            )}`}
            name={selectionGroupName ?? 'table-radio-selection'}
            checked={Boolean(props.isSelected)}
            onClick={(e) => {
              e.stopPropagation();
              props.onClick?.(e);
            }}
            data-testid={`${rowTestId}-radio`}
          />
        </TableCell>
      )}
      {props.cellData.map((d, i) => {
        const column = columns[i];
        if (visibleColumns.find((c) => c.label === column?.label)) {
          return (
            <TableCell
              showOverflow={column.showOverflow}
              truncateText={props.fixedRowHeight}
              data-testid={`${props['data-testid']}-cell-${column.label}`}
              maxWidth={props.maxColumnWidth}
              key={`${props['data-testid']}-cell-${column.label}`}
              width={props.columnsWidths?.[i]}
            >
              {d}
            </TableCell>
          );
        }
        return null;
      })}
    </TableRowStyled>
  );
};
